import React, { Component } from 'react'
import { PropTypes } from 'prop-types'

import { Button, Container, Input } from '@sainsburys-tech/fable'

import LoadingOverlay from '../LoadingOverlay'
import api from '../../utils/api'
import logger from '../../utils/logger'

import PublicDSIBHeader from '../PublicDSIBHeader'

import '../../sass/components/reset-password.scss'

class ResetPassword extends Component {
  constructor(props) {
    super(props)
    this.state = {
      password: '',
      confirmPassword: '',
      passwordFieldBlured: false,
      confirmPasswordBlured: false,
      loading: false
    }
  }

  async componentDidMount() {
    const { history } = this.props

    const search = new URLSearchParams(window.location.search)
    const userId = search.get('userId')
    const token = search.get('token')
    const timeStamp = Math.floor(search.get('timeStamp'))
    const now = Date.now()
    let tokenValid = true

    await api.checkTokenIsValid(userId, 'Default', 'ResetPassword', token)
      .then((res) => {
        if (res === true) {
          tokenValid = true
        } else if (res.status === 400) {
          tokenValid = false
        }
      })
      .catch((error) => {
        logger.logError(error)
      })

    // email is over 24hrs old so now expired
    if ((now - timeStamp) >= 8.64e+7) { // 8.64e+7 = 24hrs in milliseconds
      history.push('/resetpassword/expired')
    } else if (!tokenValid && (now - timeStamp < 8.64e+7)) { // token is invalid but less than 24hrs old so therefore it's been used already
      history.push('/resetpassword/used')
    }
  }


  checkAllFieldsValid = () => {
    const regex = /^(.{0,11}|[^0-9]*|[^A-Z]*|[a-zA-Z0-9]*)$/ // must be min length of 12, 1 uppercase, 1 number and 1 symbol
    const {
      password, confirmPassword
    } = this.state

    if (password === ''
      || confirmPassword === ''
      || (password !== confirmPassword)
      || (regex.test(password))
    ) {
      return false
    }
    return true
  }

  setPasswordBlur = () => {
    this.setState({ passwordFieldBlured: true })
  }

  setConfirmPasswordBlur = () => {
    this.setState({ confirmPasswordBlured: true })
  }

  passwordFieldError = () => {
    const { password, passwordFieldBlured } = this.state
    const regex = /^(.{0,11}|[^0-9]*|[^A-Z]*|[a-zA-Z0-9]*)$/ // must be min length of 12, 1 uppercase, 1 number and 1 symbol

    if (regex.test(password) && passwordFieldBlured) {
      return true
    }
    return false
  }

  getPasswordRequirements = () => {
    const { password } = this.state

    return [
      { label: 'at least 1 uppercase letter', isValid: /[A-Z]/.test(password) },
      { label: 'at least 1 number', isValid: /[0-9]/.test(password) },
      { label: 'at least 1 special character (!*.-?)', isValid: /[^a-zA-Z0-9]/.test(password) },
      { label: 'at least 12 characters long', isValid: password.length >= 12 }
    ]
  }

  confirmPasswordFieldError = () => {
    const { password, confirmPassword, confirmPasswordBlured } = this.state

    if (confirmPassword !== password && confirmPasswordBlured) {
      return true
    }
    return false
  }

  handleTextChange = (value, key) => {
    const nextValue = value || ''
    this.setState({ [key]: nextValue })
  }

  handleSubmit = () => {
    const { history } = this.props
    this.setState({ loading: true })

    const { password } = this.state

    const search = new URLSearchParams(window.location.search)
    const userId = search.get('userId')
    const token = search.get('token')

    api.resetPassword(userId, token, password)
      .then((res) => {
        if (res.succeeded === true) {
          history.push('/resetsuccess')
        } else {
          history.push(`/reseterror?${search}`)
        }
        this.setState({ loading: false })
      }).catch(() => history.push(`/reseterror?${search}`))
  }

  render() {
    const { password, confirmPassword, loading } = this.state
    const passwordRequirements = this.getPasswordRequirements()

    return (
      <Container as="div" className="dsib-reset">
        <PublicDSIBHeader />

        <main className="dsib-reset__content">
          <div className="dsib-reset__panel">
            <h2 className="dsib-reset__title">Reset password</h2>

            <p className="dsib-reset__intro">
              Please enter your password in the following format:
            </p>

            <ul className="dsib-reset__list">
              {passwordRequirements.map(requirement => (
                <li
                  key={requirement.label}
                  className={`dsib-reset__requirement dsib-reset__requirement--${requirement.isValid ? 'valid' : 'invalid'}`}
                >
                  {requirement.label}
                </li>
              ))}
            </ul>

            <div className="dsib-reset__form">
              <div className={`dsib-reset__field ${this.passwordFieldError() ? 'dsib-reset__field--error' : ''}`}>
                <Input
                  label="Password"
                  value={password}
                  onChange={value => this.handleTextChange(value, 'password')}
                  inputProps={{
                    id: 'txtPassword',
                    name: 'txtPassword',
                    type: 'password',
                    autoComplete: 'new-password',
                    onBlur: this.setPasswordBlur,
                  }}
                />
                {this.passwordFieldError() && (
                  <div className="dsib-reset__error" role="alert">
                    Enter password in the correct format
                  </div>
                )}
              </div>

              <div className={`dsib-reset__field ${this.confirmPasswordFieldError() ? 'dsib-reset__field--error' : ''}`}>
                <Input
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={value => this.handleTextChange(value, 'confirmPassword')}
                  inputProps={{
                    id: 'txtConfirmPassword',
                    name: 'txtConfirmPassword',
                    type: 'password',
                    autoComplete: 'new-password',
                    onBlur: this.setConfirmPasswordBlur,
                  }}
                />
                {this.confirmPasswordFieldError() && (
                  <div className="dsib-reset__error" role="alert">
                    The passwords must be the same
                  </div>
                )}
              </div>

              <div className="dsib-reset__button">
                <Button
                  isFullWidth
                  type="button"
                  disabled={!this.checkAllFieldsValid()}
                  onClick={this.handleSubmit}
                  variant="primary"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </main>

        {loading && <LoadingOverlay hasBackground text="Resetting your password..." />}
      </Container>
    )
  }
}

ResetPassword.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,

}

export default ResetPassword
